import { IAppContext } from 'App/AppContext';
import { ITaskViewLayoutState } from 'components/Tasks/TaskViews/TaskViewTypes';
import { TaskViewLayoutDTO } from 'models/dto/tasks/taskViewDTO';
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiGetTaskViewLayout, apiSaveTaskViewLayout, getLatestRevisionFromError } from 'services/Api/taskViewService';
import { apiRequest } from 'services/Auth/authConfig';
import { getTaskViewLayoutSummary, logTaskViewDiagnostic } from 'utils/taskViewDiagnostics';
import {
  getTaskViewLayoutRevision,
  hasTaskViewLayoutContent,
  readTaskViewLayout,
  writeTaskViewLayout,
} from './TaskViewLayoutModel';

export const getTaskViewLayoutScopeKey = (appContext: IAppContext): string => {
  const tenantId = appContext.user.login.tenantId || 'no-tenant';
  const userId = appContext.user.login.userId || 'no-user';

  return `${tenantId}:${userId}`;
};

interface IUseTaskViewLayoutSyncProps {
  appContext: IAppContext;
  isLoading: boolean;
  layoutStateScopeKey: string;
  // The in-memory layout document plus its dirty/sequence tracking. `dirty`
  // is the save trigger -- no serialize-and-compare guessing.
  layoutDoc: ITaskViewLayoutState;
  layoutDirty: boolean;
  layoutMutationSeq: number;
  // Replaces the whole layout document in one atomic update (a server load,
  // a SignalR echo). Clears `dirty`.
  applyRemoteLayout: (layout: ITaskViewLayoutState) => void;
  // Clears `dirty` if no edit has bumped `mutationSeq` since the save started.
  markLayoutSaved: (mutationSeq: number) => void;
  onLayoutSaveStateChange?: (state: 'saving' | 'saved' | 'error') => void;
}

const layoutSaveDebounceMs = 500;

export const useTaskViewLayoutSync = ({
  appContext,
  isLoading,
  layoutStateScopeKey,
  layoutDoc,
  layoutDirty,
  layoutMutationSeq,
  applyRemoteLayout,
  markLayoutSaved,
  onLayoutSaveStateChange,
}: IUseTaskViewLayoutSyncProps) => {
  const cacheScopeKey = getTaskViewLayoutScopeKey(appContext);
  const isLayoutStateForCurrentScope = layoutStateScopeKey === cacheScopeKey;
  const [hasLoadedServerLayout, setHasLoadedServerLayout] = useState<boolean>(false);

  const currentCacheScopeKeyRef = useRef<string>(cacheScopeKey);
  currentCacheScopeKeyRef.current = cacheScopeKey;
  const layoutRevisionRef = useRef<string | undefined>(undefined);
  // True for a brand-new user with no stored layout -- lets the first save
  // through before any server revision exists.
  const canSaveWithoutRevisionRef = useRef<boolean>(false);
  const currentLayoutLoadPromiseRef = useRef<Promise<void> | undefined>(undefined);
  const currentLayoutSavePromiseRef = useRef<Promise<boolean> | undefined>(undefined);
  const layoutSaveDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // getAccessToken read through a ref so the callbacks stay identity-stable
  // across appContext re-renders (a window resize churns the context value).
  const getAccessTokenRef = useRef(appContext.getAccessToken);
  getAccessTokenRef.current = appContext.getAccessToken;
  // Latest-refs so the stable callbacks read current values across awaits.
  const layoutDocRef = useRef(layoutDoc);
  layoutDocRef.current = layoutDoc;
  const layoutDirtyRef = useRef(layoutDirty);
  layoutDirtyRef.current = layoutDirty;
  const layoutMutationSeqRef = useRef(layoutMutationSeq);
  layoutMutationSeqRef.current = layoutMutationSeq;
  // Mount-only unmount-flush reads the latest flusher through a ref.
  const unmountFlushRef = useRef<() => void>(() => {});

  // Scope (tenant/user) change -- drop everything for the previous scope.
  useEffect(() => {
    logTaskViewDiagnostic('sync.scope.reset', { cacheScopeKey });
    if (layoutSaveDebounceTimerRef.current) {
      clearTimeout(layoutSaveDebounceTimerRef.current);
      layoutSaveDebounceTimerRef.current = undefined;
    }
    layoutRevisionRef.current = undefined;
    canSaveWithoutRevisionRef.current = false;
    currentLayoutLoadPromiseRef.current = undefined;
    currentLayoutSavePromiseRef.current = undefined;
    setHasLoadedServerLayout(false);
  }, [cacheScopeKey]);

  // PUTs one layout payload, resolving an optimistic-concurrency 409 by
  // adopting the server's latest revision and retrying once. On success the
  // save is confirmed via markLayoutSaved(savedSeq) -- which clears `dirty`
  // only if no edit has happened since the save started.
  const saveLayoutPayload = useCallback(
    async (doc: ITaskViewLayoutState, savedSeq: number): Promise<boolean> => {
      if (!layoutRevisionRef.current && !canSaveWithoutRevisionRef.current) {
        logTaskViewDiagnostic('sync.save.blockedMissingRevision', { savedSeq });

        return false;
      }

      onLayoutSaveStateChange?.('saving');
      try {
        const accessToken = await getAccessTokenRef.current(apiRequest.scopes);
        const payload = writeTaskViewLayout(doc, layoutRevisionRef.current);
        logTaskViewDiagnostic('sync.save.put', { savedSeq, payload: getTaskViewLayoutSummary(payload) });

        let saved: TaskViewLayoutDTO;
        try {
          saved = await apiSaveTaskViewLayout(payload, accessToken);
        } catch (err) {
          if ((err as { code?: string })?.code !== '409') throw err;

          const latestRevision = getLatestRevisionFromError(err);
          logTaskViewDiagnostic('sync.save.conflict', { savedSeq, latestRevision });
          if (latestRevision) {
            layoutRevisionRef.current = getTaskViewLayoutRevision(latestRevision);
          } else {
            const latest = await apiGetTaskViewLayout(accessToken);
            layoutRevisionRef.current = getTaskViewLayoutRevision(latest?.revision);
          }
          if (!layoutRevisionRef.current) throw err;

          canSaveWithoutRevisionRef.current = false;
          logTaskViewDiagnostic('sync.save.retry', { savedSeq, revision: layoutRevisionRef.current });
          saved = await apiSaveTaskViewLayout({ ...payload, revision: layoutRevisionRef.current }, accessToken);
        }

        layoutRevisionRef.current = getTaskViewLayoutRevision(saved.revision);
        canSaveWithoutRevisionRef.current = false;
        markLayoutSaved(savedSeq);
        onLayoutSaveStateChange?.('saved');
        logTaskViewDiagnostic('sync.save.done', { savedSeq, revision: layoutRevisionRef.current });

        return true;
      } catch (err) {
        logTaskViewDiagnostic('sync.save.error', {
          savedSeq,
          code: (err as { code?: string })?.code,
          status: (err as { response?: { status?: number } })?.response?.status,
        });
        onLayoutSaveStateChange?.('error');

        return false;
      }
    },
    [markLayoutSaved, onLayoutSaveStateChange],
  );

  // Runs save tasks strictly one at a time -- a save requested while another
  // is in flight chains after it (and picks up the latest doc when it runs).
  const runSaveSerially = useCallback(async (task: () => Promise<boolean>): Promise<boolean> => {
    const previous = currentLayoutSavePromiseRef.current;
    const promise = (async (): Promise<boolean> => {
      if (previous) {
        await previous.catch(() => undefined);
      }

      return task();
    })();
    currentLayoutSavePromiseRef.current = promise;
    try {
      return await promise;
    } finally {
      if (currentLayoutSavePromiseRef.current === promise) {
        currentLayoutSavePromiseRef.current = undefined;
      }
    }
  }, []);

  // Debounced save: fires only while `dirty`. One dispatch == one render, so
  // this effect never observes a half-applied layout; a server APPLY clears
  // `dirty` and cancels any pending save.
  useEffect(() => {
    if (!isLayoutStateForCurrentScope) {
      logTaskViewDiagnostic('sync.save.skipWrongScope', { layoutStateScopeKey, cacheScopeKey });

      return;
    }
    if (!hasLoadedServerLayout) {
      logTaskViewDiagnostic('sync.save.skipNotLoaded');

      return;
    }
    if (isLoading) {
      logTaskViewDiagnostic('sync.save.skipProviderLoading');

      return;
    }
    if (!layoutDirty) {
      if (layoutSaveDebounceTimerRef.current) {
        clearTimeout(layoutSaveDebounceTimerRef.current);
        layoutSaveDebounceTimerRef.current = undefined;
      }
      logTaskViewDiagnostic('sync.save.skipClean', { mutationSeq: layoutMutationSeq });

      return;
    }
    if (!layoutRevisionRef.current && !canSaveWithoutRevisionRef.current) {
      logTaskViewDiagnostic('sync.save.skipMissingRevision');

      return;
    }

    logTaskViewDiagnostic('sync.save.schedule', {
      mutationSeq: layoutMutationSeq,
      debounceMs: layoutSaveDebounceMs,
      layout: getTaskViewLayoutSummary(writeTaskViewLayout(layoutDoc, layoutRevisionRef.current)),
    });
    if (layoutSaveDebounceTimerRef.current) {
      clearTimeout(layoutSaveDebounceTimerRef.current);
    }
    layoutSaveDebounceTimerRef.current = setTimeout(() => {
      layoutSaveDebounceTimerRef.current = undefined;
      logTaskViewDiagnostic('sync.save.debounceFired');
      void runSaveSerially(() => saveLayoutPayload(layoutDocRef.current, layoutMutationSeqRef.current));
    }, layoutSaveDebounceMs);
  }, [
    isLayoutStateForCurrentScope,
    hasLoadedServerLayout,
    isLoading,
    layoutDirty,
    layoutMutationSeq,
    layoutDoc,
    cacheScopeKey,
    layoutStateScopeKey,
    runSaveSerially,
    saveLayoutPayload,
  ]);

  // Imperative save -- persists a specific layout immediately (used by the
  // "reset tab bar to defaults" action, which awaits the result).
  const saveTaskViewLayoutNow = useCallback(
    async (layout: ITaskViewLayoutState): Promise<boolean> => {
      const payload = writeTaskViewLayout(layout, layoutRevisionRef.current);
      logTaskViewDiagnostic('sync.saveNow', { layout: getTaskViewLayoutSummary(payload) });
      if (layoutSaveDebounceTimerRef.current) {
        clearTimeout(layoutSaveDebounceTimerRef.current);
        layoutSaveDebounceTimerRef.current = undefined;
      }

      return runSaveSerially(() => saveLayoutPayload(layout, layoutMutationSeqRef.current));
    },
    [runSaveSerially, saveLayoutPayload],
  );

  // Flush a pending save on unmount so a debounced edit is not lost.
  unmountFlushRef.current = () => {
    if (layoutSaveDebounceTimerRef.current) {
      clearTimeout(layoutSaveDebounceTimerRef.current);
      layoutSaveDebounceTimerRef.current = undefined;
    }
    if (layoutDirtyRef.current) {
      logTaskViewDiagnostic('sync.unmount.flush');
      void runSaveSerially(() => saveLayoutPayload(layoutDocRef.current, layoutMutationSeqRef.current));
    }
  };
  useEffect(() => {
    return () => unmountFlushRef.current();
  }, []);

  // GET the canonical layout -- used on mount and on a SignalR resync. Live
  // layoutMutated events do NOT come through here; they carry the full layout
  // and are applied directly via applyLayoutFromEvent (no GET round-trip).
  const loadTaskViewLayout = useCallback(
    async (reason: string = 'unspecified') => {
      logTaskViewDiagnostic('sync.load.enter', {
        reason,
        cacheScopeKey,
        hasInFlightLoad: Boolean(currentLayoutLoadPromiseRef.current),
      });

      if (currentLayoutLoadPromiseRef.current) {
        logTaskViewDiagnostic('sync.load.joinInFlight', { reason });

        return currentLayoutLoadPromiseRef.current;
      }

      const requestScopeKey = cacheScopeKey;
      const loadPromise = (async (): Promise<void> => {
        try {
          logTaskViewDiagnostic('sync.load.get', { reason, requestScopeKey });
          const accessToken = await getAccessTokenRef.current(apiRequest.scopes);
          const layout = await apiGetTaskViewLayout(accessToken);
          if (currentCacheScopeKeyRef.current !== requestScopeKey) {
            logTaskViewDiagnostic('sync.load.ignoreStaleScope', {
              reason,
              requestScopeKey,
              currentScopeKey: currentCacheScopeKeyRef.current,
            });

            return;
          }

          setHasLoadedServerLayout(true);
          layoutRevisionRef.current = getTaskViewLayoutRevision(layout?.revision);

          const hasStoredLayout = hasTaskViewLayoutContent(layout);
          canSaveWithoutRevisionRef.current = !layoutRevisionRef.current && !hasStoredLayout;
          logTaskViewDiagnostic('sync.load.received', {
            reason,
            hasStoredLayout,
            revision: layoutRevisionRef.current,
            layout: getTaskViewLayoutSummary(layout),
          });

          if (!hasStoredLayout) {
            // No server layout yet -- keep the local default doc; the first
            // user edit establishes the baseline (canSaveWithoutRevision).
            logTaskViewDiagnostic('sync.load.noStoredLayout', { reason });

            return;
          }

          applyRemoteLayout(readTaskViewLayout(layout));
          logTaskViewDiagnostic('sync.load.applied', { reason });
        } catch (err) {
          logTaskViewDiagnostic('sync.load.error', {
            reason,
            code: (err as { code?: string })?.code,
            status: (err as { response?: { status?: number } })?.response?.status,
          });
          // API failure keeps local fallback state active. Flip the flag so the
          // UI does not sit on the loading state forever, but block writes until
          // a canonical revision is loaded.
          canSaveWithoutRevisionRef.current = false;
          setHasLoadedServerLayout(true);
        }
      })().finally(() => {
        if (currentLayoutLoadPromiseRef.current === loadPromise) {
          currentLayoutLoadPromiseRef.current = undefined;
        }
        logTaskViewDiagnostic('sync.load.finally', { reason });
      });
      currentLayoutLoadPromiseRef.current = loadPromise;

      return loadPromise;
    },
    [cacheScopeKey, applyRemoteLayout],
  );

  // Applies a layoutMutated SignalR event. The event carries the full layout,
  // so no GET is needed. Skips the originating tab's own echo (same revision)
  // and, if this tab has unsaved edits, keeps them -- it just adopts the new
  // revision so its pending save PUTs cleanly (last edit wins).
  const applyLayoutFromEvent = useCallback(
    (layout: TaskViewLayoutDTO) => {
      const revision = getTaskViewLayoutRevision(layout?.revision);
      if (revision && revision === layoutRevisionRef.current) {
        logTaskViewDiagnostic('sync.event.skipSelfEcho', { revision });

        return;
      }

      layoutRevisionRef.current = revision;
      if (layoutDirtyRef.current) {
        logTaskViewDiagnostic('sync.event.skipApplyDirty', { revision });

        return;
      }

      applyRemoteLayout(readTaskViewLayout(layout));
      logTaskViewDiagnostic('sync.event.applied', { revision });
    },
    [applyRemoteLayout],
  );

  return {
    hasLoadedServerLayout,
    loadTaskViewLayout,
    applyLayoutFromEvent,
    saveTaskViewLayoutNow,
  };
};
